import React, { useState, useEffect } from 'react';
import { MagnifyingGlass, SlidersHorizontal, X, GridFour, List } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const AuthorFilters = ({ 
  filters, 
  onFilterChange, 
  filterOptions,
  showLegendaryFilters = false,
  showCommunityFilters = false,
  viewMode = 'grid',
  onViewModeChange
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    onFilterChange({ ...filters, search: searchTerm });
  };

  // Helper to handle select changes that might use "all" as placeholder
  const handleSelectChange = (key, value) => {
    onFilterChange({ ...filters, [key]: value === 'all' ? '' : value });
  };

  const clearFilters = () => {
    setSearchTerm('');
    onFilterChange({
      search: '',
      alphabet: '',
      literary_movement: '',
      century: '',
      genre: '',
      language: '',
      gender: '',
      state: '',
      country: '',
      is_verified: null,
      is_featured: null,
      is_award_winner: null,
      sort_by: 'name'
    });
  };

  const activeFilterCount = Object.values(filters).filter(v => v && v !== 'name').length;

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <form onSubmit={handleSearchSubmit} className="flex gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by name, pen name, or themes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button type="submit">Search</Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="gap-2"
        >
          <SlidersHorizontal size={18} />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-1 px-2 py-0.5 bg-primary text-primary-foreground rounded-full text-xs">
              {activeFilterCount}
            </span>
          )}
        </Button>
      </form>

      {/* View Mode Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onViewModeChange('grid')}
          >
            <GridFour size={16} />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onViewModeChange('list')}
          >
            <List size={16} />
          </Button>
        </div>

        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-2">
            <X size={14} /> Clear all filters
          </Button>
        )}
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="p-6 border border-border rounded-lg bg-muted/20">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Alphabet Filter */}
            <div>
              <label className="block text-sm font-medium mb-2">Alphabet</label>
              <Select
                value={filters.alphabet || 'all'}
                onValueChange={(value) => handleSelectChange('alphabet', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  <SelectItem value="अ">अ–ज्ञ (Hindi)</SelectItem>
                  {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 
                    'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'].map(letter => (
                    <SelectItem key={letter} value={letter}>{letter}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Literary Heritage Filters */}
            {showLegendaryFilters && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">Literary Movement</label>
                  <Select
                    value={filters.literary_movement || 'all'}
                    onValueChange={(value) => handleSelectChange('literary_movement', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any</SelectItem>
                      {filterOptions.movements?.map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Century</label>
                  <Select
                    value={filters.century || 'all'}
                    onValueChange={(value) => handleSelectChange('century', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any</SelectItem>
                      {filterOptions.centuries?.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Gender</label>
                  <Select
                    value={filters.gender || 'all'}
                    onValueChange={(value) => handleSelectChange('gender', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any</SelectItem>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Award Winner</label>
                  <Select
                    value={filters.is_award_winner === true ? 'yes' : filters.is_award_winner === false ? 'no' : 'all'}
                    onValueChange={(value) => onFilterChange({ 
                      ...filters, 
                      is_award_winner: value === 'yes' ? true : value === 'no' ? false : null 
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Community Writers Filters */}
            {showCommunityFilters && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">Verified</label>
                  <Select
                    value={filters.is_verified === true ? 'yes' : filters.is_verified === false ? 'no' : 'all'}
                    onValueChange={(value) => onFilterChange({ 
                      ...filters, 
                      is_verified: value === 'yes' ? true : value === 'no' ? false : null 
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any</SelectItem>
                      <SelectItem value="yes">Verified ✓</SelectItem>
                      <SelectItem value="no">Not Verified</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Featured</label>
                  <Select
                    value={filters.is_featured === true ? 'yes' : filters.is_featured === false ? 'no' : 'all'}
                    onValueChange={(value) => onFilterChange({ 
                      ...filters, 
                      is_featured: value === 'yes' ? true : value === 'no' ? false : null 
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any</SelectItem>
                      <SelectItem value="yes">Featured ⭐</SelectItem>
                      <SelectItem value="no">Not Featured</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">State</label>
                  <Select
                    value={filters.state || 'all'}
                    onValueChange={(value) => handleSelectChange('state', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any</SelectItem>
                      {filterOptions.states?.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Country</label>
                  <Select
                    value={filters.country || 'all'}
                    onValueChange={(value) => handleSelectChange('country', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any</SelectItem>
                      {filterOptions.countries?.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Common Filters */}
            <div>
              <label className="block text-sm font-medium mb-2">Genre</label>
              <Select
                value={filters.genre || 'all'}
                onValueChange={(value) => handleSelectChange('genre', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  {filterOptions.genres?.map(g => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Language</label>
              <Select
                value={filters.language || 'all'}
                onValueChange={(value) => handleSelectChange('language', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  {filterOptions.languages?.map(l => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Sort By */}
            <div>
              <label className="block text-sm font-medium mb-2">Sort By</label>
              <Select
                value={filters.sort_by || 'name'}
                onValueChange={(value) => onFilterChange({ ...filters, sort_by: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Alphabetical</SelectItem>
                  <SelectItem value="works_count">Most Works</SelectItem>
                  <SelectItem value="followers_count">Most Followers</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthorFilters;
